import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, KeyRound, ShieldCheck, ShieldOff, Save, X, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  superadmin: { text: 'Superadmin', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  admin: { text: 'Admin', className: 'bg-navy-100 text-navy-900 border-navy-300' },
  user: { text: 'User', className: 'bg-gray-100 text-gray-700 border-gray-300' },
};

const AdminUsers = ({ token, currentUserEmail }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [createForm, setCreateForm] = useState({ email: '', password: '', role: 'admin' });
  const [creating, setCreating] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  // Per-row password-reset panel
  const [resetForUserId, setResetForUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/users`, { headers: authHeaders });
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password) return;
    if (createForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/auth/users`, createForm, { headers: authHeaders });
      toast.success(`User ${createForm.email} created`);
      setCreateForm({ email: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (user, newRole) => {
    try {
      await axios.patch(
        `${API}/auth/users/${user.id}`,
        { role: newRole },
        { headers: authHeaders }
      );
      toast.success(`${user.email} is now ${newRole}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.patch(
        `${API}/auth/users/${user.id}`,
        { is_active: !user.is_active },
        { headers: authHeaders }
      );
      toast.success(`${user.email} ${user.is_active ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${user.id}`, { headers: authHeaders });
      toast.success(`Deleted ${user.email}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (user) => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.patch(
        `${API}/auth/users/${user.id}`,
        { password: resetPassword },
        { headers: authHeaders }
      );
      toast.success(`Password reset for ${user.email}`);
      setResetForUserId(null);
      setResetPassword('');
      setShowResetPassword(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-panel">
      {/* Create new user */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-semibold text-navy-900 mb-1 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-gold-500" />
          Add a new admin
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Creates a login for another team member. They can sign in with the email
          and password below.
        </p>
        <form onSubmit={handleCreate} className="grid sm:grid-cols-4 gap-3">
          <Input
            type="email"
            required
            placeholder="email@example.com"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            className="sm:col-span-2"
            data-testid="new-user-email"
          />
          <div className="relative">
            <Input
              type={showCreatePassword ? 'text' : 'password'}
              required
              placeholder="Initial password (min 6)"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              className="pr-9"
              data-testid="new-user-password"
            />
            <button
              type="button"
              onClick={() => setShowCreatePassword((v) => !v)}
              tabIndex={-1}
              aria-label={showCreatePassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 hover:text-navy-900"
              data-testid="new-user-password-toggle"
            >
              {showCreatePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <select
            value={createForm.role}
            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
            data-testid="new-user-role"
          >
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="superadmin">Superadmin</option>
          </select>
          <Button
            type="submit"
            disabled={creating}
            className="sm:col-span-4 bg-navy-900 hover:bg-navy-800 text-white"
            data-testid="new-user-submit"
          >
            {creating ? 'Creating…' : 'Create user'}
          </Button>
        </form>
      </div>

      {/* User list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="text-base font-semibold text-navy-900">Existing users</h3>
          <span className="text-xs text-gray-500" data-testid="users-count">
            {users.length} total
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No users found.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {users.map((u) => {
              const isSelf = u.email === currentUserEmail;
              const isSuper = u.role === 'superadmin';
              const roleMeta = ROLE_LABELS[u.role] || ROLE_LABELS.user;
              return (
                <li key={u.id} className="px-5 py-4" data-testid={`user-row-${u.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-navy-900">{u.email}</span>
                        {isSelf && (
                          <span className="text-[10px] uppercase tracking-wider bg-gold-100 text-gold-700 px-1.5 py-0.5 rounded">
                            You
                          </span>
                        )}
                        <span
                          className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${roleMeta.className}`}
                        >
                          {roleMeta.text}
                        </span>
                        {!u.is_active && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                            Disabled
                          </span>
                        )}
                      </div>
                      {u.created_at && (
                        <div className="text-xs text-gray-500">
                          Created {new Date(u.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* Role selector — superadmins locked */}
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u, e.target.value)}
                        disabled={isSuper}
                        className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isSuper ? 'Superadmin role is locked' : 'Change role'}
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Superadmin</option>
                      </select>

                      {/* Enable / Disable — hidden for self & superadmin */}
                      {!isSuper && !isSelf && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(u)}
                          className="h-8 text-xs"
                          data-testid={`toggle-active-${u.id}`}
                        >
                          {u.is_active ? (
                            <>
                              <ShieldOff className="w-3.5 h-3.5 mr-1" /> Disable
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Enable
                            </>
                          )}
                        </Button>
                      )}

                      {/* Reset password */}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setResetForUserId(resetForUserId === u.id ? null : u.id);
                          setResetPassword('');
                          setShowResetPassword(false);
                        }}
                        className="h-8 text-xs"
                        data-testid={`reset-toggle-${u.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
                      </Button>

                      {/* Delete — hidden for self & superadmin */}
                      {!isSuper && !isSelf && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(u)}
                          className="h-8 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Password reset inline panel */}
                  {resetForUserId === u.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                      <div className="relative flex-1 min-w-[220px]">
                        <Input
                          type={showResetPassword ? 'text' : 'password'}
                          placeholder="New password (min 6 characters)"
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          className="pr-9 h-9 text-sm"
                          data-testid={`reset-input-${u.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPassword((v) => !v)}
                          tabIndex={-1}
                          aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 hover:text-navy-900"
                          data-testid={`reset-toggle-visibility-${u.id}`}
                        >
                          {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleResetPassword(u)}
                        disabled={resetting}
                        className="h-9 bg-navy-900 hover:bg-navy-800 text-white"
                        data-testid={`reset-save-${u.id}`}
                      >
                        <Save className="w-3.5 h-3.5 mr-1" />
                        {resetting ? 'Saving…' : 'Save new password'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setResetForUserId(null);
                          setResetPassword('');
                          setShowResetPassword(false);
                        }}
                        className="h-9"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">
        Superadmins have full access and cannot be demoted, disabled, or deleted from
        this panel — that guardrail prevents accidental lock-out. To retire a
        superadmin, promote another user to superadmin first, then delete the
        seeded account directly in the database.
      </p>
    </div>
  );
};

export default AdminUsers;
